import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import t from '../theme';

export default function ApprovalsPage() {
  const [pending,   setPending]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [processing, setProcessing] = useState({});
  const [notes,     setNotes]     = useState({});

  const load = useCallback(async () => {
    try {
      const r = await axios.get('/api/students/pending');
      setPending(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handle = async (studentId, action) => {
    setProcessing(p => ({ ...p, [studentId]: true }));
    try {
      await axios.post(`/api/students/${studentId}/${action}`, {
        notes: notes[studentId] || '',
      });
      load();
    } catch (e) { console.error(e); }
    finally { setProcessing(p => ({ ...p, [studentId]: false })); }
  };

  const fmtDate = d => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
  const calcAge = dob => {
    if (!dob) return '';
    const diff = Date.now() - new Date(dob).getTime();
    return Math.floor(diff / (365.25 * 24 * 60 * 60 * 1000));
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 600, color: t.text, margin: 0, letterSpacing: '-0.3px' }}>
          Student Approvals
        </h2>
        <p style={{ fontSize: '13px', color: t.text3, margin: '4px 0 0' }}>
          {pending.length} pending application{pending.length !== 1 ? 's' : ''}
        </p>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', color: t.text3, padding: '48px' }}>Loading...</div>
      ) : pending.length === 0 ? (
        <div style={{ ...t.card, padding: '48px', textAlign: 'center' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.3 }}>✓</div>
          <p style={{ fontSize: '15px', fontWeight: 500, color: t.text, margin: 0 }}>No pending applications</p>
          <p style={{ fontSize: '13px', color: t.text3, margin: '6px 0 0' }}>New applications will appear here.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {pending.map(s => (
            <div key={s.id} style={{ ...t.card, padding: '20px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                flexWrap: 'wrap', gap: '16px' }}>

                {/* Student info */}
                <div style={{ flex: 1, minWidth: '240px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '8px',
                      background: t.accentLight, border: `1px solid ${t.accentBorder}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '14px', fontWeight: 700, color: t.accent }}>
                      {s.full_name[0]}
                    </div>
                    <div>
                      <div style={{ fontSize: '15px', fontWeight: 500, color: t.text }}>{s.full_name}</div>
                      <div style={{ fontSize: '12px', color: t.text3 }}>@{s.requested_username}</div>
                    </div>
                    <span style={{ background: 'rgba(196,154,60,0.12)', border: '1px solid rgba(196,154,60,0.25)',
                      color: t.accent, padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: 600 }}>
                      Pending
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '6px' }}>
                    {[
                      { label: 'Email',      value: s.email },
                      { label: 'Age',        value: s.date_of_birth ? `${calcAge(s.date_of_birth)} yrs (${fmtDate(s.date_of_birth)})` : '—' },
                      { label: 'Guardian',   value: s.guardian_name || '—' },
                      { label: 'Phone',      value: s.guardian_phone || '—' },
                      { label: 'Experience', value: s.experience || '—' },
                    ].map(({ label, value }) => (
                      <div key={label}>
                        <span style={{ fontSize: '11px', color: t.text3, textTransform: 'uppercase',
                          letterSpacing: '0.5px' }}>{label} </span>
                        <span style={{ fontSize: '13px', color: t.text2 }}>{value}</span>
                      </div>
                    ))}
                  </div>

                  {/* Why learn / Goals */}
                  {s.why_learn && (
                    <div style={{ marginTop: '10px', padding: '10px 12px', background: t.surface3,
                      borderRadius: '8px', border: `1px solid ${t.border}` }}>
                      <div style={{ fontSize: '11px', color: t.text3, textTransform: 'uppercase',
                        letterSpacing: '0.5px', marginBottom: '4px' }}>Why they want to learn</div>
                      <p style={{ fontSize: '13px', color: t.text2, margin: 0, lineHeight: 1.6 }}>{s.why_learn}</p>
                    </div>
                  )}
                  {s.goals && (
                    <div style={{ marginTop: '8px', padding: '10px 12px', background: t.surface3,
                      borderRadius: '8px', border: `1px solid ${t.border}` }}>
                      <div style={{ fontSize: '11px', color: t.text3, textTransform: 'uppercase',
                        letterSpacing: '0.5px', marginBottom: '4px' }}>Goals</div>
                      <p style={{ fontSize: '13px', color: t.text2, margin: 0, lineHeight: 1.6 }}>{s.goals}</p>
                    </div>
                  )}

                  {/* Optional rejection/approval notes */}
                  <div style={{ marginTop: '12px' }}>
                    <textarea
                      style={{ ...t.textarea, fontSize: '12px', resize: 'vertical', height: '60px' }}
                      placeholder="Optional note to student (included in approval/rejection email)..."
                      value={notes[s.id] || ''}
                      onChange={e => setNotes(n => ({ ...n, [s.id]: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: '140px' }}>
                  <button
                    style={{ background: t.greenLight, border: '1px solid rgba(76,175,125,0.3)',
                      color: t.green, padding: '10px 16px', borderRadius: '8px', fontSize: '13px',
                      fontWeight: 600, cursor: processing[s.id] ? 'not-allowed' : 'pointer',
                      fontFamily: t.font, opacity: processing[s.id] ? 0.6 : 1 }}
                    onClick={() => handle(s.id, 'approve')}
                    disabled={processing[s.id]}>
                    {processing[s.id] ? '...' : '✓ Approve'}
                  </button>
                  <button
                    style={{ ...t.btnDanger, padding: '10px 16px', fontSize: '13px',
                      opacity: processing[s.id] ? 0.6 : 1 }}
                    onClick={() => handle(s.id, 'reject')}
                    disabled={processing[s.id]}>
                    ✕ Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
